/**
 * Framing nails that fit the crew's gun.
 *
 * A framing nailer takes one kind of nail. A coil gun takes coils, a 21° gun
 * takes plastic-strip nails, a 30° gun takes paper-tape nails, and a box of the
 * wrong one is a box the crew cannot use: they drive to the store, or they
 * wait. Jonathon, 2026-10-15: "Pablo has a 30 degree nail gun, which means if
 * he's the crew then 30 degree framing nails need to be selected when
 * relevant."
 *
 * So the gun is a fact about the crew (`crews.framing_nailer`) and the gun a
 * nail fits is a fact about the product (`products.framing_nailer`). Where a
 * material order carries framing nails that do not fit the crew's gun, the
 * order takes the ones that do. The template, the deck over and a line
 * somebody picked by hand all buy whatever nail they were written with; this
 * is the one place that turns it into the crew's. ADR 0287.
 *
 * A nail with no gun set is never swapped: the galvanized ring-shank nails on a
 * deck are 30° but are chosen because the lumber is treated, and a crew with a
 * different gun swapping them for bright nails would be the worse mistake.
 */

import type { Cents } from "./money.js";
import type { GeneratedLine, GeneratorProduct } from "./generator.js";

/** The guns the office tells apart. */
export type FramingNailer = "coil" | "21" | "30";

export const FRAMING_NAILERS: readonly FramingNailer[] = ["coil", "21", "30"];

/** How the office says it. */
export function framingNailerLabel(nailer: FramingNailer): string {
  return nailer === "coil" ? "Coil" : `${nailer}°`;
}

/** A stored value, or null when it is not one of the guns above. */
export function asFramingNailer(value: unknown): FramingNailer | null {
  return FRAMING_NAILERS.find((n) => n === value) ?? null;
}

/** What this needs to know about a product. */
export interface NailProduct {
  id: string;
  name: string | null;
  /** The gun this nail fits, when it is a framing nail. */
  framing_nailer?: string | null;
}

/**
 * The gun the order's nails have to fit.
 *
 * Every crew with a gun on record must agree: two crews on one job with
 * different guns is a question for a person, not a guess, so it answers null
 * and nothing is swapped. A crew with no gun on record has not been asked and
 * does not count against the one that has.
 */
export function nailerForCrews(
  crews: readonly ({ framing_nailer?: string | null } | null | undefined)[],
): FramingNailer | null {
  const guns = new Set(
    crews
      .map((crew) => asFramingNailer(crew?.framing_nailer))
      .filter((gun): gun is FramingNailer => gun !== null),
  );
  return guns.size === 1 ? ([...guns][0] ?? null) : null;
}

/**
 * The crews whose gun counts for one material order.
 *
 * The work orders on the order's own estimate, narrowed to the ones for the
 * order's trade when any are — a deck crew's gun says nothing about the nails
 * on the roof. A work order with no trades written counts for every trade.
 *
 * `tradeOnly` stops it there: no crew for the trade answers none, so a caller
 * that knows the crew the template will send asks that one before the crews
 * doing the other trades.
 */
export function crewsForOrder<C>(
  order: { trade?: string | null },
  workOrders: readonly { trades?: readonly string[] | null; crew: C | null }[],
  options: { tradeOnly?: boolean } = {},
): C[] {
  const withCrew = workOrders.filter((wo) => wo.crew !== null);
  const trade = (order.trade ?? "").trim();
  const forTrade =
    trade === ""
      ? []
      : withCrew.filter((wo) => {
          const trades = wo.trades ?? [];
          return trades.length === 0 || trades.includes(trade);
        });
  const counted =
    forTrade.length > 0 || options.tradeOnly ? forTrade : withCrew;
  return counted.map((wo) => wo.crew as C);
}

/**
 * The crews whose gun decides an order's framing nails, in ADR 0287's order.
 *
 * The crew on the work order for the order's trade; before there is one, the
 * crew the template will send (`fallbackCrew`); and only then every crew on
 * the estimate. A gutter crew already booked says nothing about the roof, so
 * it never stands in for the template's roofing crew.
 */
export function crewsForNails<C>(
  order: { trade?: string | null },
  workOrders: readonly { trades?: readonly string[] | null; crew: C | null }[],
  fallbackCrew: C | null | undefined,
): C[] {
  const forTrade = crewsForOrder(order, workOrders, { tradeOnly: true });
  if (forTrade.length > 0) return forTrade;
  if (fallbackCrew !== null && fallbackCrew !== undefined)
    return [fallbackCrew];
  return crewsForOrder(order, workOrders);
}

/**
 * The nail to buy instead of `product`, or null to keep it.
 *
 * Null when the product is not a framing nail, already fits, no gun is known,
 * or the catalogue has no nail for that gun — an order with the wrong nail on
 * it is still better than an order missing its nails.
 */
export function framingNailFor<P extends NailProduct>(
  product: P | null | undefined,
  nailer: FramingNailer | null,
  catalogue: readonly P[],
): P | null {
  if (!product || nailer === null) return null;
  const fits = asFramingNailer(product.framing_nailer);
  if (fits === null || fits === nailer) return null;
  const candidates = catalogue
    .filter((p) => asFramingNailer(p.framing_nailer) === nailer)
    // Alphabetical, so the same order picks the same nail every time.
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  return candidates[0] ?? null;
}

/** Said on a line whose nail was changed for the crew's gun. */
export function framingNailNote(nailer: FramingNailer): string {
  return `${framingNailerLabel(nailer)} nails for the crew's gun`;
}

/** One line on an order whose nail does not fit the crew's gun. */
export interface FramingNailMismatch<P extends NailProduct> {
  itemId: string;
  /** The nail on the line now. */
  current: P;
  /** The nail that fits. */
  fit: P;
}

/**
 * The lines on an order that the crew's gun cannot shoot.
 *
 * For an order that already exists: the crew may have changed since it was
 * written, or the nail was picked by hand. The editor offers the switch rather
 * than making it, because an order is somebody's work (ADR 0287).
 */
export function framingNailMismatches<P extends NailProduct>(
  items: readonly { id: string; product_id: string | null }[],
  nailer: FramingNailer | null,
  catalogue: readonly P[],
): FramingNailMismatch<P>[] {
  if (nailer === null) return [];
  const byId = new Map(catalogue.map((p) => [p.id, p]));
  const out: FramingNailMismatch<P>[] = [];
  for (const item of items) {
    const current = item.product_id ? byId.get(item.product_id) : undefined;
    const fit = framingNailFor(current, nailer, catalogue);
    if (current && fit) out.push({ itemId: item.id, current, fit });
  }
  return out;
}

/**
 * A freshly generated material order, with its framing nails made the crew's.
 *
 * The quantity stays what the measurement worked out: a box is a box, and the
 * nail the template names is the one its coverage was written for. The price
 * is the new nail's, from the same supplier the line was going to.
 */
export function fitFramingNails(
  lines: readonly GeneratedLine[],
  nailer: FramingNailer | null,
  catalogue: readonly GeneratorProduct[],
  costOf: (product: GeneratorProduct, line: GeneratedLine) => Cents,
): GeneratedLine[] {
  if (nailer === null) return [...lines];
  const byId = new Map(catalogue.map((p) => [p.id, p]));
  return lines.map((line) => {
    const fit = framingNailFor(byId.get(line.product_id), nailer, catalogue);
    if (!fit) return line;
    return {
      ...line,
      product_id: fit.id,
      description: [fit.category, fit.name].filter(Boolean).join(" | "),
      unit: fit.unit,
      unitPrice: costOf(fit, line),
      calc: `${line.calc} · ${framingNailNote(nailer)}`,
    };
  });
}
